import {
  IImageObjectTaskItemSession,
  IImageObjectTaskSession,
  IPlayImageObjectTask,
  tImageObjectAnswer
} from 'common/modules/ReactShared';
import { t } from 'i18n';
import { useCallback, useEffect } from 'react';
import { usePromptContext } from 'students/views/shared/hooks';
import { customMediaQuery } from 'students/views/shared/styled';
import styled from 'styled-components';
import Streak from '../../common/components/Streak';
import { usePlayAnswers, useStreakFactor } from '../../common/hooks';
import PlayTaskScreenSwitcher from '../../common/screens/PlayTaskScreenSwitcher';
import { IPlayTaskProps, tPlayTaskComponentType } from '../types';
import { ISelectAnswerPayload } from './ImageObjectItem';
import PlayImageObjectMainScreen from './PlayImageObjectMainScreen';
import bgImage from './assets/ImageObjectTaskBg.png';
import bgImageWebp from './assets/ImageObjectTaskBg.webp';

const PlayImageObjectTask: tPlayTaskComponentType<
  IPlayTaskProps<IPlayImageObjectTask, IImageObjectTaskSession>
> = ({ task, taskSession, isCompleting, onFinish, onNext, onAnswer, className }) => {
  const { items, instruction } = task;
  const answersFn = usePlayAnswers<tImageObjectAnswer>();

  const { streakNumber, streakPercent, onTheFirstTry } = useStreakFactor();
  const { makeDirty } = usePromptContext();

  useEffect(() => {
    if (taskSession?.taskItemSessions) {
      answersFn(taskSession.taskItemSessions);
    } else {
      answersFn(
        items.map((item, index) => {
          return {
            id: index,
            taskItemId: item.id,
            completed: false,
            mistakesCount: 0,
            skipped: false,
            url: ''
          };
        })
      );
    }

    onTheFirstTry(false);
  }, [taskSession, items, onTheFirstTry, answersFn]);

  const updateAnswer = (itemId: number, data: Partial<tImageObjectAnswer[number]>) => {
    const answerIndex = answersFn().findIndex(
      (answer: IImageObjectTaskItemSession) => answer.taskItemId === itemId
    );

    if (answerIndex < 0) return;

    const { mistakesCount, skipped } = data;

    const answer = answersFn()[answerIndex];
    const newAnswerData = { ...answer, ...data };

    answersFn([
      ...answersFn().slice(0, answerIndex),
      newAnswerData,
      ...answersFn().slice(answerIndex + 1)
    ]);

    onAnswer && onAnswer(newAnswerData);

    onTheFirstTry(!(mistakesCount || skipped));
  };

  const handleSelectAnswer = (payload: ISelectAnswerPayload) => {
    const { itemId, mistakesCount } = payload;

    updateAnswer(itemId, {
      mistakesCount,
      skipped: false
    });

    makeDirty(true);
  };

  const handleSkip = (itemId: number) => {
    updateAnswer(itemId, {
      skipped: true
    });
  };

  const handleFinishTask = () => {
    onNext && onNext();
  };

  const submitTask = useCallback(() => {
    onFinish && onFinish(answersFn());
  }, [onFinish, answersFn]);

  const handleCompleteTask = (goToFinishScreen: () => void) => () => {
    submitTask();

    goToFinishScreen();
  };

  return (
    <>
      <Streak streakNumber={streakNumber} streakPercent={streakPercent} />
      <PlayTaskScreenSwitcher
        isCompleting={isCompleting}
        onExitFinishScreen={handleFinishTask}
        className={className}
        startScreenHeading={
          instruction ||
          t('frontend.lesson_task.tasks.play.image_object.start_screen.heading')
        }
        itemsExist={!!items.length}
        taskStatus={taskSession?.status}
        completeTask={submitTask}
        taskColor="#212543"
      >
        {(goToFinishScreen) => (
          <PlayImageObjectMainScreen
            task={task}
            onSelectAnswer={handleSelectAnswer}
            onComplete={handleCompleteTask(goToFinishScreen)}
            onSkip={handleSkip}
          />
        )}
      </PlayTaskScreenSwitcher>
    </>
  );
};

export default PlayImageObjectTask;

PlayImageObjectTask.backgroundComponent = styled.div`
  background-color: #353b67;
  background-image: linear-gradient(180deg, #353b67 0%, #212543 100%);

  ${customMediaQuery('desktop')} {
    background-repeat: no-repeat, no-repeat;
    background-position: center;
    background-size: cover, auto;
    background-image: url(${bgImage}), linear-gradient(180deg, #353b67 0%, #212543 100%);

    .webp & {
      background-image: url(${bgImageWebp}),
        linear-gradient(180deg, #353b67 0%, #212543 100%);
    }
  }
`;
PlayImageObjectTask.bgColor = '#353b67';
